import { useEffect, useRef, useState } from "react";

const UPDATE_CHECK_INTERVAL_MS = 60 * 60 * 1000;

function isNativeRuntime() {
  return "__TAURI_INTERNALS__" in window;
}

export function PwaUpdatePrompt() {
  const [waitingWorker, setWaitingWorker] = useState<ServiceWorker | null>(null);
  const [dismissed, setDismissed] = useState(false);
  const registrationRef = useRef<ServiceWorkerRegistration | null>(null);
  const reloadOnControllerChangeRef = useRef(false);

  useEffect(() => {
    if (isNativeRuntime() || !("serviceWorker" in navigator) || !window.isSecureContext) return;

    let disposed = false;
    let updateTimer: number | undefined;
    const watchWorker = (worker: ServiceWorker) => {
      worker.addEventListener("statechange", () => {
        if (!disposed && worker.state === "installed" && navigator.serviceWorker.controller) {
          setWaitingWorker(worker);
          setDismissed(false);
        }
      });
    };

    const register = async () => {
      const registration = await navigator.serviceWorker.register("/sw.js", { scope: "/" });
      if (disposed) return;
      registrationRef.current = registration;
      if (registration.waiting && navigator.serviceWorker.controller) setWaitingWorker(registration.waiting);
      registration.addEventListener("updatefound", () => {
        if (registration.installing) watchWorker(registration.installing);
      });
      updateTimer = window.setInterval(() => void registration.update(), UPDATE_CHECK_INTERVAL_MS);
      await registration.update();
    };

    const handleControllerChange = () => {
      if (reloadOnControllerChangeRef.current) window.location.reload();
    };
    navigator.serviceWorker.addEventListener("controllerchange", handleControllerChange);
    void register().catch(() => undefined);

    return () => {
      disposed = true;
      if (updateTimer) window.clearInterval(updateTimer);
      navigator.serviceWorker.removeEventListener("controllerchange", handleControllerChange);
    };
  }, []);

  const applyUpdate = () => {
    const worker = waitingWorker ?? registrationRef.current?.waiting;
    if (!worker) return;
    reloadOnControllerChangeRef.current = true;
    worker.postMessage({ type: "SKIP_WAITING" });
  };

  if (!waitingWorker || dismissed) return null;

  return (
    <aside className="pwa-update-prompt" role="status" aria-live="polite">
      <span className="pwa-update-prompt__text">新版本已准备好</span>
      <button className="pwa-update-prompt__action" type="button" onClick={applyUpdate}>
        更新
      </button>
      <button className="pwa-update-prompt__dismiss" type="button" onClick={() => setDismissed(true)} aria-label="稍后更新">
        x
      </button>
    </aside>
  );
}
